/**
 * API: oportunidades activas (Radar) + acciones (favoritar / descartar).
 *
 * Sprint 3 (lista) + Sprint 4 (matching score) + Sprint 8.5
 * (favoritas / dismiss).
 *
 * Sprint 19 UX-1 (mayo 21, 2026): el concepto de "Destacada"
 * (`is_featured`, `featured_priority`) se eliminó por completo. El
 * Radar se ordena 100% por match_score + closes_at.
 */

import * as Sentry from "@sentry/react";

import { ApiError, apiErrorFromResponse, safeFetch } from "../errors";
import { supabase } from "../supabase";
import { API_V1, getAccessToken } from "./_client";

export interface OpportunitySourceDTO {
  code: string;
  name: string;
  type: string;
  /** national | international — pestaña del Radar. */
  origin: string;
  /** direct | via_partner | info_only — quién puede aplicar. */
  eligibility: string;
}

export interface OpportunityMatchDTO {
  /** Score de relevancia 0-100. */
  score: number;
  /** Razones humanas que explican el score. Una string por regla disparada. */
  reasons: string[];
  /** Heurística UX: true cuando score >= 60. */
  is_recommended: boolean;
}

export interface OpportunityDTO {
  id: string;
  external_id: string | null;
  title: string;
  description: string | null;
  sector: string | null;
  sectors_extra: string[];
  amount_total_cop: string | null;
  amount_per_project_max_cop: string | null;
  currency: string;
  opens_at: string | null;
  closes_at: string | null;
  /**
   * Recibe postulaciones "hasta agotar cupos" (issue #692): no tiene fecha de
   * cierre por diseño, y el Radar la muestra 90 días desde `opens_at`.
   * Opcional para las respuestas anteriores al campo.
   */
  until_quota_exhausted?: boolean;
  source_url: string;
  status: string;
  source: OpportunitySourceDTO;
  /** Último HTTP status observado al visitar source_url. 0 = error de red. */
  url_http_status: number | null;
  /** Timestamp ISO del último check de la URL. */
  url_last_checked_at: string | null;
  /** Populado cuando la llamada incluyó `?municipality=DANE`. */
  match: OpportunityMatchDTO | null;
  // Sprint 19 UX-1 (mayo 21, 2026): los campos `is_featured` y
  // `featured_priority` se eliminaron junto con el concepto de
  // "Destacada". El Radar se ordena 100% por match_score + closes_at.
  /**
   * Acciones del usuario actual — Sprint 8.5. Se populan cuando la
   * request es autenticada con `municipality`. Sin auth quedan en
   * false.
   */
  is_favorited: boolean;
  is_dismissed: boolean;
  /**
   * Expediente vivo (ni rechazado, ni cerrado, ni borrado) que el tenant
   * ya abrió desde esta convocatoria — issue #541: un solo expediente vivo
   * por convocatoria. `null` si no hay o si la request es anónima. Con id,
   * la tarjeta ofrece "Ver expediente" en vez de "Crear expediente".
   */
  existing_project_id: string | null;
}

export interface ListOpportunitiesParams {
  sector?: string;
  source_code?: string;
  /** Texto libre; ILIKE case-insensitive en título + descripción. */
  search?: string;
  /** Filtro de urgencia: solo convocatorias que cierran en ≤ N días. */
  max_days_to_close?: number;
  /** Código DANE (5 dígitos). Cuando se envía, cada opp incluye `match` y se ordena por score DESC. */
  municipality?: string;
  /** Solo favoritas del usuario actual (requiere auth + municipality). */
  favorited_only?: boolean;
  /** Incluir descartadas (default false las oculta). */
  include_dismissed?: boolean;
  /** Filtra por origen de la fuente (pestaña del Radar). */
  origin?: "national" | "international";
  limit?: number;
  offset?: number;
}

export interface OpportunitiesPage {
  items: OpportunityDTO[];
  /** Total sin paginar (del header X-Total-Count). */
  total: number;
}

export async function fetchOpportunities(
  params: ListOpportunitiesParams = {},
): Promise<OpportunitiesPage> {
  const qs = new URLSearchParams();
  if (params.sector) qs.set("sector", params.sector);
  if (params.source_code) qs.set("source_code", params.source_code);
  if (params.search) qs.set("search", params.search);
  if (params.max_days_to_close !== undefined) {
    qs.set("max_days_to_close", String(params.max_days_to_close));
  }
  if (params.municipality) qs.set("municipality", params.municipality);
  if (params.favorited_only) qs.set("favorited_only", "true");
  if (params.include_dismissed) qs.set("include_dismissed", "true");
  if (params.origin) qs.set("origin", params.origin);
  if (params.limit) qs.set("limit", String(params.limit));
  if (params.offset) qs.set("offset", String(params.offset));
  const query = qs.toString();

  // Necesitamos leer el header X-Total-Count → no usamos `request<T>` directo.
  const token = await getAccessToken();
  const headers = new Headers();
  headers.set("Accept", "application/json");
  if (token) headers.set("Authorization", `Bearer ${token}`);

  const res = await safeFetch(`${API_V1}/opportunities${query ? `?${query}` : ""}`, { headers });

  if (res.status === 401) {
    await supabase.auth.signOut();
  }
  if (!res.ok) {
    throw await apiErrorFromResponse(res);
  }

  const items = (await res.json()) as OpportunityDTO[];
  // Fallback a items.length cuando el header no viaja (ej: tests que usan
  // Response sintético sin headers, o respuestas cacheadas por el service worker).
  const totalHeader = res.headers.get("X-Total-Count");
  const total = totalHeader !== null ? Number(totalHeader) : items.length;
  return { items, total };
}

/**
 * Marca/desmarca una convocatoria como favorita o descartada.
 *
 * Idempotente — el backend usa ON CONFLICT DO NOTHING para INSERT y un
 * DELETE silencioso para remove. Doble click no duplica filas.
 *
 * El API se mapea como REST: el verbo HTTP indica si se aplica o se
 * quita la acción.
 *
 * - apply=true → POST /opportunities/{id}/{action}?dane=...
 * - apply=false → DELETE /opportunities/{id}/{action}?dane=...
 */
export async function setOpportunityAction(args: {
  opportunityId: string;
  action: "favorite" | "dismiss";
  dane: string;
  apply: boolean;
}): Promise<void> {
  const { opportunityId, action, dane, apply } = args;
  const token = await getAccessToken();
  if (!token) throw new ApiError(401, "Auth requerida para favoritar/descartar.");

  const headers = new Headers();
  headers.set("Authorization", `Bearer ${token}`);

  const url = `${API_V1}/opportunities/${encodeURIComponent(opportunityId)}/${action}?dane=${encodeURIComponent(dane)}`;
  const res = await safeFetch(url, { method: apply ? "POST" : "DELETE", headers });

  if (res.status === 401) {
    await supabase.auth.signOut();
  }
  if (!res.ok && res.status !== 204) {
    throw await apiErrorFromResponse(res);
  }
}

/**
 * Registra que el usuario VIO una convocatoria concreta en el ledger
 * legal de descubrimiento (`discovery_events`, hash-chain con valor para
 * la comisión — CLAUDE.md regla 8). Escribe un evento `opportunity_viewed`
 * vía `POST /opportunities/{id}/view?dane={DANE}` (204 No Content; 404 si
 * la convocatoria no existe).
 *
 * **Best-effort fire-and-forget**: a diferencia de `setOpportunityAction`,
 * esta función NUNCA lanza ni le muestra un error al funcionario —
 * registrar la vista jamás debe bloquear la UI. Es el mismo contrato que
 * la telemetría de `analytics_events` (ver `track()` en
 * `lib/analytics.ts`); la diferencia es el destino: este evento va al
 * ledger legal, no a `/events/track`.
 *
 * **Pero no en silencio** (oct-2026, tras encontrar `discovery_events` en
 * cero en producción sin poder saber si alguna vista se había perdido):
 * - va con `keepalive`, como `track()`, para sobrevivir a la navegación;
 * - si la vista no queda registrada (red, 4xx o 5xx), avisa a Sentry con
 *   un mensaje curado y solo el status — sin ids ni datos del funcionario
 *   (R-SEC-004) — y libera el dedupe para que el siguiente clic reintente.
 *
 * A propósito NO forzamos `signOut()` en un 401 como hacen `request()` y
 * `setOpportunityAction()`: un ping de telemetría no debe desautenticar al
 * usuario. Si el token de verdad expiró, el próximo request real lo hará.
 *
 * El endpoint requiere JWT (`get_current_user`): sin sesión activa no hay
 * tenant que registre la vista, así que es un no-op silencioso.
 *
 * No es idempotente a nivel de API por diseño — cada request que SÍ llega
 * al backend agrega un eslabón nuevo a la cadena. Dispárala solo cuando
 * el usuario abre/ve una convocatoria específica, nunca por cada card del
 * listado (eso inflaría la cadena con impresiones).
 *
 * Sprint Blindaje — SEC-5 (julio 2026): guard de dedupe por sesión. El
 * mismo `opportunityId` puede re-disparar este handler más de una vez en
 * la misma sesión de navegación (re-render de la card, remount del
 * listado al cambiar de tab/filtro, doble click accidental en "Ver
 * original") sin que el usuario haya "visto" la convocatoria una segunda
 * vez de verdad — cada re-fire agregaba un eslabón redundante a la
 * cadena. `sentViewsThisSession` vive a nivel de módulo (no en
 * localStorage): se resetea con cada carga de página, que es justo el
 * alcance de "sesión" que necesitamos — un refresh completo sí debe
 * poder volver a registrar la vista.
 */
const sentViewsThisSession = new Set<string>();

export async function recordOpportunityView(args: {
  opportunityId: string;
  dane: string;
}): Promise<void> {
  const { opportunityId, dane } = args;
  if (sentViewsThisSession.has(opportunityId)) return;
  try {
    const token = await getAccessToken();
    if (!token) return;

    // Marcamos ANTES del fetch (no en un .then/onSuccess) para que dos
    // llamadas casi simultáneas (doble click) no ganen la carrera y
    // ambas pasen el check de arriba.
    sentViewsThisSession.add(opportunityId);

    const headers = new Headers();
    headers.set("Authorization", `Bearer ${token}`);

    const url = `${API_V1}/opportunities/${encodeURIComponent(opportunityId)}/view?dane=${encodeURIComponent(dane)}`;
    const res = await safeFetch(url, { method: "POST", headers, keepalive: true });
    if (!res.ok) vistaNoRegistrada(opportunityId, String(res.status));
  } catch {
    // safeFetch envuelve los fallos de red en ApiError.
    vistaNoRegistrada(opportunityId, "network");
  }
}

function vistaNoRegistrada(opportunityId: string, status: string): void {
  sentViewsThisSession.delete(opportunityId);
  Sentry.captureMessage("discovery_view_not_recorded", { level: "warning", tags: { status } });
}
